<template>
  <PopupContainer>
    <div class="sc-drawer-cont">
      <el-input
        v-model="filterWord"
        :prefix-icon="Search"
        placeholder="输入条件名(或拼音)搜索"
        clearable
        class="filter-kw"
      />

      <div class="sc-drawer-tips">
        <el-icon><Warning /></el-icon><span>可筛选项进行隐藏、拖拽排序</span>
      </div>

      <el-scrollbar>
        <draggable
          :list="nameToLabelList"
          item-key="name"
          @change="onOrderChange"
        >
          <template #item="{ element }">
            <div
              v-show="pinyinMatch(element.label, filterWord)"
              class="sc-cond-item"
            >
              <el-checkbox
                v-model="visibleItems[element.name]"
                @change="setItemVisible(element.name)"
              >
                {{ element.label }}
              </el-checkbox>
            </div>
          </template>
        </draggable>
      </el-scrollbar>
    </div>

    <template #footer>
      <div class="sc-drawer-footer">
        <el-checkbox
          v-model="checkAll"
          :indeterminate="isIndeterminate"
          @change="handleCheckAllChange"
        >
          全选
        </el-checkbox>
        <div>
          <el-button @click="restoreDefault">恢复默认</el-button>
          <el-button type="primary" @click="popup.done(true)">确定</el-button>
        </div>
      </div>
    </template>
  </PopupContainer>
</template>

<script lang="ts" setup>
import { reactive, ref, computed, nextTick } from 'vue';
import { Warning, Search } from '@element-plus/icons-vue';
import { usePopup, PopupCtx } from '../../core/EasyPopup';
import Draggable from 'vuedraggable';
import { pinyinMatch } from '../../core/CommonInternal';
import PopupContainer from '../../common/popup/PopupContainer.vue';

const props = defineProps<{
  layout: () => any;
  popupCtx?: PopupCtx<true>;
}>();

const popup = usePopup(props);

const layout = props.layout();
// 列的name => title映射
const sortedItems = layout.control.sorted;

const isHidden = layout.isHidden;
const showItem = layout.showItem;
const moveItem = layout.moveItem;
const restore = layout.restore;

// 列的name => visible映射
const visibleItems = reactive<Record<string, boolean>>({});

const nameToLabelList = ref<{ name: string; label: string }[]>(sortedItems());

nameToLabelList.value?.forEach(
  (e) => (visibleItems[e.name] = !isHidden(e.name))
);

function setItemVisible(name: string) {
  const visible = visibleItems[name];
  showItem(name, visible);

  popup.call('fieldChange');
}

// 移动元素
function onOrderChange(evt: any) {
  moveItem(evt.moved.element.name, evt.moved.newIndex);

  popup.call('fieldChange');
}

/// 全选等操作
const checkAll = computed({
  // 是否全选
  get: () => !Object.values(visibleItems).includes(false),
  set: (val) => val
});

const isIndeterminate = computed({
  // 是否有选择项
  get: () => {
    const values = Object.values(visibleItems);
    return values.includes(true) && values.includes(false);
  },
  set: (val) => val
});

const handleCheckAllChange = (show: any) => {
  for (let name in visibleItems) {
    visibleItems[name] = show;
    showItem(name, show, false); // 不立即保存
  }

  showItem('', false);

  popup.call('fieldChange');
};

const restoreDefault = () => {
  restore?.();
  popup.call('fieldChange');

  nextTick(() => popup.done(true));
};

const filterWord = ref('');
</script>

<style lang="scss" scoped>
.sc-drawer-cont {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.filter-kw {
  padding: 12px 16px 0px 16px;

  // fix erp2
  :deep(.el-input__wrapper) {
    box-shadow: 0 0 0 1px var(--el-input-border-color, var(--el-border-color))
      inset;
  }
}

.sc-cond-item {
  box-sizing: border-box;
  height: 36px;
  padding: 0 16px;

  &:hover {
    background-color: #f3f4f6;
  }
}

.sc-drawer-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .el-checkbox__input {
    padding-top: 2px;
  }

  .el-button {
    height: 32px;
    border: var(--el-border);
    border-color: var(--el-button-border-color);
  }
}

:deep(.el-drawer__body) {
  padding: 0;
  overflow: hidden;
}

.sc-drawer-tips {
  padding: 16px;
  font-size: 12px;
  font-weight: 500;
  color: #8f959e;

  .el-icon {
    top: 2px;
    margin-right: 4px;
    transform: rotate(180deg);
  }
}
</style>
